// src/pages/Transactions.jsx
import {
  AccountBalance,
  Add,
  ArrowDownward,
  ArrowUpward,
  AttachMoney,
  Build,
  CardGiftcard,
  Close,
  Delete,
  DirectionsBus,
  FilterList,
  Flight,
  Gavel,
  Home,
  LabelOutlined,
  LocalHospital,
  LocalMovies,
  People,
  Pets,
  School,
  Search,
  // category icons
  ShoppingCart,
  Sort,
  Spa,
  Work,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CssVarsProvider,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemContent,
  Option,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useColorScheme,
  useTheme,
} from "@mui/joy";
import { useMediaQuery } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

/**
 * Transactions page (logic-focused update)
 *
 * Props:
 *  - onAddTransaction(savedTx) optional callback
 *  - categories: array of {id, name, subCategories} OR object mapping label -> {id, dates}
 *  - transactions: optional array of transactions (server-provided)
 */
export default function Transactions({
  onAddTransaction,
  categories = [],
  transactions,
}) {
  // form state
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState(false);
  const descInputRef = React.useRef(null);

  const [amountError, setAmountError] = useState(false);
  const amountInputRef = React.useRef(null);
  const [showTooltips, setShowTooltips] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString());
  const [transactionType, setTransactionType] = useState("expense");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [useManualDescription, setUseManualDescription] = useState(true);

  // list + UI state
  const [localTransactions, setLocalTransactions] = useState(
    Array.isArray(transactions) ? transactions : [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [addOpen, setAddOpen] = useState(true);

  // loading / skeleton
  const [isLoading, setIsLoading] = useState(false);
  const loadingTimerRef = useRef(null);

  // pagination (frontend-only)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // theme
  const { mode } = useColorScheme();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // keyword map for auto category detection (keeps your mapping)
  const keywordMap = {
    food: ["sandwich", "burger", "pizza", "restaurant", "coffee"],
    entertainment: ["netflix", "spotify", "movie", "cinema", "game"],
    utilities: ["electricity", "water", "internet", "gas", "bill"],
    income: ["salary", "deposit", "bonus", "freelance"],
  };

  // -------------------------
  // helpers: categories normalization
  // -------------------------
  const normalizedCategories = React.useMemo(() => {
    if (!categories) return [];
    if (Array.isArray(categories)) {
      return categories
        .map((c) => ({
          id: c?.id ?? c?.value ?? null,
          name: c?.name ?? c?.label ?? String(c?.id ?? c ?? ""),
          subCategories: c?.subCategories ?? [],
        }))
        .filter(Boolean);
    }
    // if object keyed by label -> { id, dates } or similar
    if (typeof categories === "object") {
      return Object.entries(categories).map(([label, obj]) => ({
        id: obj && typeof obj === "object" ? (obj.id ?? null) : null,
        name: label,
        subCategories: [],
      }));
    }
    return [];
  }, [categories]);

  // find category object by id (string/number flexible)
  const findCategoryById = (id) => {
    if (id == null || id === "") return null;
    return (
      normalizedCategories.find((c) => String(c.id) === String(id)) || null
    );
  };

  // find subcategory by id across all categories
  const findSubcategoryById = (id) => {
    if (id == null || id === "") return null;
    for (const cat of normalizedCategories) {
      if (cat.subCategories && Array.isArray(cat.subCategories)) {
        const found = cat.subCategories.find(
          (sub) => String(sub.id) === String(id),
        );
        if (found) return found;
      }
    }
    return null;
  };

  // get subcategories for selected category
  const selectedCategory = findCategoryById(selectedCategoryId);
  const subCategories = selectedCategory?.subCategories || [];

  // -------------------------
  // helpers: timestamps & keys (robust)
  // -------------------------
  const parseTime = (val) => {
    if (val == null) return NaN;
    // numbers
    if (typeof val === "number" && Number.isFinite(val)) return val;
    try {
      let s = String(val).trim();
      if (s === "") return NaN;
      // if pure integer timestamp string
      if (/^-?\d+$/.test(s)) {
        const n = Number(s);
        if (!Number.isNaN(n)) return n;
      }
      // common ISO / RFC formats
      let t = Date.parse(s);
      if (!Number.isNaN(t)) return t;
      // try removing repeated commas/spaces
      t = Date.parse(s.replace(/,\s*/g, " "));
      if (!Number.isNaN(t)) return t;
      // try replacing ordinal suffixes (1st, 2nd) - remove 'st/nd/rd/th'
      t = Date.parse(s.replace(/(\d+)(st|nd|rd|th)/gi, "$1"));
      if (!Number.isNaN(t)) return t;
      // final fallback to Date constructor
      t = new Date(s).getTime();
      if (!Number.isNaN(t)) return t;
    } catch (err) {
      // ignore and return NaN below
    }
    return NaN;
  };

  const txTimestampCandidates = [
    "created_at",
    "createdAt",
    "timestamp",
    "dateTime",
    "datetime",
    "date",
  ];

  const getTxTimestamp = (tx) => {
    if (!tx || typeof tx !== "object") return 0;
    for (const k of txTimestampCandidates) {
      if (tx[k] != null) {
        const t = parseTime(tx[k]);
        if (!Number.isNaN(t)) return t;
      }
    }
    if (tx._clientCreatedAt != null) {
      const t = parseTime(tx._clientCreatedAt);
      if (!Number.isNaN(t)) return t;
    }
    // last resort: if tx.date exists try robust parsing again
    if (tx.date != null) {
      const t = parseTime(tx.date);
      if (!Number.isNaN(t)) return t;
    }
    return 0;
  };

  const txKey = (tx) => {
    if (!tx) return String(Math.random());
    if (tx.id) return String(tx.id);
    const datePart = tx.date || tx._clientCreatedAt || "";
    const desc = tx.description || "";
    return `${datePart}:::${desc}:::${tx._clientCreatedAt ?? ""}`;
  };

  // -------------------------
  // icon mapping for categories
  // -------------------------
  const categoryIconMap = {
    none: <LabelOutlined sx={{ fontSize: 14,color:mode==="light" ? "#454545":null }} />,
    "food & groceries": <ShoppingCart sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null}} />,
    transportation: <DirectionsBus sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "home & utilities": <Home sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "personal & self-care": <Spa sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    health: <LocalHospital sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "entertainment & leisure": <LocalMovies sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    education: <School sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "finance & banking": <AccountBalance sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "gifts & donations": <CardGiftcard sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    travel: <Flight sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "family & children": <People sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "work & business": <Work sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    pets: <Pets sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "household maintenance": <Build sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    "taxes & government": <Gavel sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
    income: <AttachMoney sx={{ fontSize: 14 ,color:mode==="light" ? "#454545":null }} />,
  };

  const getIconForCategoryName = (name) => {
    if (!name) return categoryIconMap.none;
    const key = String(name).trim().toLowerCase();
    // exact or contains matching fallback
    if (categoryIconMap[key]) return categoryIconMap[key];
    // try to match substrings to be more forgiving
    const mapEntries = Object.entries(categoryIconMap);
    for (const [k, v] of mapEntries) {
      if (key.includes(k.split(" ")[0])) return v;
    }
    return categoryIconMap.none;
  };

  // small helper to get icon directly from id (safe)
  const getIconForCategoryId = (id) => {
    const found = findCategoryById(id);
    return getIconForCategoryName(found?.name);
  };

  // -------------------------
  // auto-detect category by keywords
  // -------------------------
  const detectCategory = (description) => {
    if (!description) return null;
    const desc = description.toLowerCase();
    for (const [label, words] of Object.entries(keywordMap)) {
      if (words.some((w) => desc.includes(w))) {
        return (
          normalizedCategories.find(
            (c) => String(c.name).toLowerCase() === label,
          ) || null
        );
      }
    }
    return null;
  };

  // -------------------------
  // initialize local transactions (prop or fetch)
  // -------------------------
  useEffect(() => {
    if (!Array.isArray(transactions) || transactions.length === 0) return;
    setLocalTransactions((prev) => {
      const map = new Map();
      prev.forEach((p) => map.set(txKey(p), p));
      transactions.forEach((t) => map.set(txKey(t), t));
      return Array.from(map.values());
    });
  }, [transactions]);
  const API_URL = process.env.VERCEL_URL;

  useEffect(() => {
    if (Array.isArray(transactions) && transactions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/transactions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data)) return;
        const normalized = data.map((tx) => {
          const copy = { ...tx };
          if (copy.category_name) return copy;
          if (
            copy.category &&
            typeof copy.category === "object" &&
            copy.category.name
          ) {
            copy.category_name = copy.category.name;
            return copy;
          }
          if (copy.category_id != null) {
            const found = findCategoryById(copy.category_id);
            if (found) copy.category_name = found.name;
          }
          return copy;
        });
        if (!cancelled) setLocalTransactions(normalized);
      } catch (err) {
        console.error("Error fetching transactions on mount:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------
  // alerts helper
  // -------------------------
  const pushAlert = (message, severity = "success", ttl = 3500) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => setAlerts((prev) => prev.filter((a) => a.id !== id)), ttl);
  };

  const removeAlert = (id) =>
    setAlerts((prev) => prev.filter((a) => a.id !== id));

  // -------------------------
  // helper: trigger skeleton loading (debounced)
  // -------------------------
  const triggerListLoading = (minMs = 250) => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    setIsLoading(true);
    loadingTimerRef.current = setTimeout(() => {
      setIsLoading(false);
      loadingTimerRef.current = null;
    }, minMs);
  };

  // -------------------------
  // filters + sorting (fixed tie-breaker to ensure newest-first when requested)
  // -------------------------
  const filteredSortedTransactions = React.useMemo(() => {
    let fromTs = null;
    let toTs = null;
    if (filterFrom) {
      const d = new Date(`${filterFrom}T00:00:00`);
      fromTs = d.getTime();
    }
    if (filterTo) {
      const d = new Date(`${filterTo}T23:59:59.999`);
      toTs = d.getTime();
    }

    const filtered = localTransactions.filter((tx) => {
      if (searchTerm) {
        const s = (tx.description || "").toLowerCase();
        if (!s.includes(searchTerm.toLowerCase())) return false;
      }

      if (filterCategoryId) {
        const possibleId =
          tx.category_id ?? (tx.category && tx.category.id) ?? tx.category;
        if (possibleId == null) return false;
        if (String(possibleId) !== String(filterCategoryId)) return false;
      }

      if (fromTs !== null || toTs !== null) {
        const ts = getTxTimestamp(tx) || 0;
        if (fromTs !== null && ts < fromTs) return false;
        if (toTs !== null && ts > toTs) return false;
      }

      return true;
    });

    const mapped = filtered
      .map((tx, idx) => ({ tx, idx, ts: getTxTimestamp(tx) || 0 }))
      .sort((a, b) => {
        if (a.ts !== b.ts) {
          return sortOrder === "newest" ? b.ts - a.ts : a.ts - b.ts;
        }
        // timestamps equal: prefer more recently inserted item for "newest"
        if (a.idx !== b.idx) {
          return sortOrder === "newest" ? b.idx - a.idx : a.idx - b.idx;
        }
        // final stable fallback: compare id strings
        const ida = a.tx?.id ?? "";
        const idb = b.tx?.id ?? "";
        if (ida !== idb) {
          return sortOrder === "newest"
            ? String(idb).localeCompare(String(ida))
            : String(ida).localeCompare(String(idb));
        }
        return 0;
      })
      .map(({ tx }) => tx);

    return mapped;
  }, [
    localTransactions,
    searchTerm,
    filterFrom,
    filterTo,
    filterCategoryId,
    sortOrder,
  ]);

  // whenever filters/search/sort change, show skeleton and reset page
  useEffect(() => {
    setPage(1);
    triggerListLoading(300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterFrom, filterTo, filterCategoryId, sortOrder]);

  // ensure page is within bounds when filtered data changes
  useEffect(() => {
    const total = Math.max(
      1,
      Math.ceil(filteredSortedTransactions.length / pageSize),
    );
    if (page > total) setPage(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTransactions, filteredSortedTransactions, pageSize]);

  // -------------------------
  // submit new transaction
  // -------------------------
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setDescriptionError(false);
    setAmountError(false);
    setShowTooltips(false);

    let finalDescription = description;

    // If using subcategory mode, use the subcategory name as description
    if (!useManualDescription && selectedSubcategoryId) {
      const sub = findSubcategoryById(selectedSubcategoryId);
      if (sub) {
        finalDescription = sub.name;
      }
    }

    const descEmpty = !finalDescription || !finalDescription.trim();
    const amtTrim = String(amount ?? "").trim();
    const amtEmpty = amtTrim === "";

    if (descEmpty || amtEmpty) {
      if (descEmpty) setDescriptionError(true);
      if (amtEmpty) setAmountError(true);
      setShowTooltips(true);
      if (descEmpty && useManualDescription) descInputRef.current?.focus?.();
      else amountInputRef.current?.focus?.();
      return;
    }

    const parsed = parseFloat(amtTrim);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setAmountError(true);
      setShowTooltips(true);
      amountInputRef.current?.focus?.();
      return;
    }

    const signedAmount =
      transactionType === "income" ? Math.abs(parsed) : -Math.abs(parsed);

    // chosen category: prefer explicit select, else autodetect by description
    let chosenCategory = null;
    if (selectedCategoryId != null && selectedCategoryId !== "") {
      chosenCategory = findCategoryById(selectedCategoryId) || null;
    }
    if (!chosenCategory)
      chosenCategory = detectCategory(finalDescription) || null;

    let isoDate;
    try {
      const dateOnly = (String(date) || "").split("T")[0];
      const now = new Date();
      const iso = new Date(
        `${dateOnly}T${now.toTimeString().split(" ")[0]}`,
      ).toISOString();
      isoDate = iso;
    } catch {
      isoDate = new Date().toISOString();
    }

    const newTransaction = {
      description: finalDescription.trim(),
      amount: signedAmount,
      date: isoDate,
      category_id: chosenCategory?.id ?? null,
      category_name: chosenCategory?.name ?? null,
    };

    try {
      // show skeleton while adding
      setIsLoading(true);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(newTransaction),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to save transaction (${res.status}) ${text}`);
      }

      const savedTx = await res.json();

      if (!savedTx.category_id && chosenCategory)
        savedTx.category_id = chosenCategory.id;
      if (!savedTx.category_name && chosenCategory)
        savedTx.category_name = chosenCategory.name;
      if (!savedTx.date) savedTx.date = newTransaction.date;

      const anyServerTime = txTimestampCandidates
        .map((k) => savedTx?.[k])
        .find(Boolean);
      if (!anyServerTime) savedTx._clientCreatedAt = new Date().toISOString();

      setLocalTransactions((prev) => {
        const k = txKey(savedTx);
        if (prev.some((p) => txKey(p) === k)) return prev;
        return [savedTx, ...prev];
      });

      if (typeof onAddTransaction === "function") {
        try {
          onAddTransaction(savedTx);
        } catch (err) {
          console.warn("onAddTransaction threw:", err);
        }
      }

      pushAlert("Transaction added successfully", "success");

      // reset form
      setDescription("");
      setAmount("");
      setTransactionType("expense");
      setDate(new Date().toISOString());
      setSelectedCategoryId(null);
      setSelectedSubcategoryId("");
      setUseManualDescription(true);

      // short delay to avoid layout glitches
      setTimeout(() => setIsLoading(false), 240);
    } catch (err) {
      console.error("Error adding transaction:", err);
      pushAlert("Failed to add transaction", "danger");
      setIsLoading(false);
    }
  };

  // -------------------------
  // delete transaction
  // -------------------------
  const handleDelete = async (id) => {
    if (!id) return;
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/transactions/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to delete transaction (${res.status}) ${text}`);
      }
      setLocalTransactions((prev) =>
        prev.filter((tx) => String(tx.id) !== String(id)),
      );
      pushAlert("Transaction deleted", "success");
      // short delay for skeleton
      setTimeout(() => setIsLoading(false), 200);
    } catch (err) {
      console.error("Error deleting transaction:", err);
      pushAlert("Failed to delete transaction", "danger");
      setIsLoading(false);
    }
  };

  // -------------------------
  // resolve category name for display
  // -------------------------
  const resolveCategoryName = (tx) => {
    if (!tx) return null;
    if (tx.category_name) return tx.category_name;
    if (tx.category && typeof tx.category === "object" && tx.category.name)
      return tx.category.name;
    const possibleId =
      tx.category_id ?? (tx.category && tx.category.id) ?? tx.category;
    if (possibleId != null) {
      const found = findCategoryById(possibleId);
      if (found) return found.name;
    }
    return null;
  };

  // -------------------------
  // formatting helpers
  // -------------------------
  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // -------------------------
  // pagination: compute paginated slice & totals
  // -------------------------
  const totalItems = filteredSortedTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  // ensure page in range
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const paginatedTransactions = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredSortedTransactions.slice(start, end);
  }, [filteredSortedTransactions, page, pageSize]);

  const clearFilters = () => {
    setSearchTerm("");
    setFilterFrom("");
    setFilterTo("");
    setFilterCategoryId("");
    setSortOrder("newest");
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <CssVarsProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          width: "100%",
          py: { xs: 2, sm: 2 },
          height:{ xs: "105%", sm: null }
        }}
      >
        <Accordion
          expanded={addOpen}
          onChange={(e, expanded) => setAddOpen(expanded)}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: "90%", md: "60%" },
            boxShadow: "none",
            bgcolor: "transparent",
            borderRadius: 1,
            "& .MuiAccordionSummary-root": {
              minHeight: 0,
              padding: { xs: "6px 8px", sm: "6px 12px" },
            },
            "& .MuiAccordionSummary-content": { margin: 0 },
            "& .MuiAccordionDetails-root": { padding: 0 },
            borderBottom:
              mode === "light"
                ? "0.2px solid hsla(240, 7.70%, 2.50%, 0.15)"
                : "0.5px solid rgba(229, 231, 235, 0.14)",
          }}
        >
          <AccordionSummary
            expandIcon={
              <motion.span
                animate={{ rotate: addOpen ? 180 : 0 }}
                transition={{ duration: 0.25 }}
                style={{ display: "inline-flex" }}
              >
                <ExpandMoreIcon />
              </motion.span>
            }
            aria-controls="add-transaction-content"
            id="add-transaction-header"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: { xs: 1, sm: 1.5 },
              width: "100%",
            }}
          >
            <Typography
              level={isSmall ? "h4" : "h3"}
              fontFamily="monospace"
              sx={{ mb: 3 }}
            >
              Add a Transaction
            </Typography>
          </AccordionSummary>

          <AnimatePresence initial={false}>
            {addOpen && (
              <motion.div
                key="add-details"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28, ease: "easeInOut" }}
                style={{
                  overflow: "auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <AccordionDetails sx={{ width: "100%" }}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.36, ease: "easeOut" }}
                    sx={{
                      width: "100%",
                      maxWidth: { xs: "100%", sm: "90%", md: "90%" },
                      display: "flex",
                      justifyContent: { xs: "flex-start", sm: "center" }, // xs: start, sm+: center
                      p: { xs: 2, sm: 3 },
                    }}
                  >
                    <form onSubmit={handleSubmit} style={{ width: "90%" }}>
                      <Stack spacing={2}>
                        <FormControl error={descriptionError}>
                          <FormLabel>Description</FormLabel>
                          <Tooltip
                            title="Description is required"
                            open={!!descriptionError && showTooltips}
                            placement="bottom"
                            arrow
                            color="danger"
                            variant="soft"
                          >
                            <Input
                              placeholder="e.g., Sandwich"
                              value={description}
                              onChange={(e) => {
                                setDescription(e.target.value);
                                if (descriptionError)
                                  setDescriptionError(false);
                                if (showTooltips) setShowTooltips(false);
                              }}
                              ref={descInputRef}
                              error={descriptionError}
                              disabled={!useManualDescription}
                            />
                          </Tooltip>
                        </FormControl>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          sx={{ width: "100%" }}
                        >
                          <FormControl
                            sx={{ flex: { xs: "1", sm: "0 0 auto" } }}
                          >
                            <FormLabel>Type</FormLabel>
                            <ButtonGroup
                              variant="outlined"
                              size={isSmall ? "sm" : "md"}
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              <Button
                                variant={
                                  transactionType === "income"
                                    ? "solid"
                                    : "plain"
                                }
                                color="success"
                                onClick={() => setTransactionType("income")}
                                sx={{ flex: { xs: 1, sm: "none" } }}
                              >
                                + Income
                              </Button>
                              <Button
                                variant={
                                  transactionType === "expense"
                                    ? "solid"
                                    : "plain"
                                }
                                color="danger"
                                onClick={() => setTransactionType("expense")}
                                sx={{ flex: { xs: 1, sm: "none" } }}
                              >
                                - Expense
                              </Button>
                            </ButtonGroup>
                          </FormControl>

                          <FormControl error={amountError} sx={{ flex: 1 }}>
                            <FormLabel>Amount</FormLabel>
                            <Tooltip
                              title="Amount is required and must be positive"
                              open={!!amountError && showTooltips}
                              placement="bottom"
                              arrow
                              color="danger"
                              variant="soft"
                            >
                              <Input
                                placeholder="0.00"
                                type="number"
                                value={amount}
                                onChange={(e) => {
                                  setAmount(e.target.value);
                                  if (amountError) setAmountError(false);
                                  if (showTooltips) setShowTooltips(false);
                                }}
                                ref={amountInputRef}
                                error={amountError}
                              />
                            </Tooltip>
                          </FormControl>
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          sx={{ width: "100%" }}
                        >
                          <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Category</FormLabel>
                            <Select
                              placeholder="Select category"
                              value={selectedCategoryId ?? ""}
                              startDecorator={getIconForCategoryId(
                                selectedCategoryId,
                              )}
                              onChange={(e, value) => {
                                setSelectedCategoryId(value || null);
                                setSelectedSubcategoryId("");
                              }}
                            >
                              <Option value="">
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {getIconForCategoryName("None")}
                                  <Typography fontFamily="monospace">
                                    None
                                  </Typography>
                                </Stack>
                              </Option>

                              {transactionType === "income"
                                ? normalizedCategories
                                    .filter(
                                      (c) =>
                                        String(c.name).toLowerCase() ===
                                          "income" ||
                                        String(c.name)
                                          .toLowerCase()
                                          .includes("income"),
                                    )
                                    .map((c) => (
                                      <Option
                                        key={String(c.id ?? "")}
                                        value={String(c.id ?? "")}
                                      >
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          {getIconForCategoryName(c.name)}
                                          <Typography fontFamily="monospace">
                                            {c.name}
                                          </Typography>
                                        </Stack>
                                      </Option>
                                    ))
                                : normalizedCategories
                                    .filter(
                                      (c) =>
                                        String(c.name).toLowerCase() !==
                                        "income",
                                    )
                                    .map((c) => (
                                      <Option
                                        key={String(c.id ?? "")}
                                        value={String(c.id ?? "")}
                                      >
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          {getIconForCategoryName(c.name)}
                                          <Typography fontFamily="monospace">
                                            {c.name}
                                          </Typography>
                                        </Stack>
                                      </Option>
                                    ))}
                            </Select>
                            {/* <FormHelperText>
                              {transactionType === "income"
                                ? "Showing Income categories only"
                                : ""}
                            </FormHelperText> */}
                          </FormControl>

                          <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Date</FormLabel>
                            <Input
                              type="date"
                              value={(date || "").split("T")[0]}
                              onChange={(e) => {
                                const selected = e.target.value;
                                const now = new Date();
                                const newDate = new Date(
                                  `${selected}T${now.toTimeString().split(" ")[0]}`,
                                ).toISOString();
                                setDate(newDate);
                              }}
                            />
                          </FormControl>
                        </Stack>

                        {selectedCategoryId && subCategories.length > 0 && (
                          <FormControl
                            orientation="horizontal"
                            sx={{ justifyContent: "space-between" }}
                          >
                            <FormLabel>
                              Select Subcategory from{" "}
                              {selectedCategory?.name}{" "}
                            </FormLabel>
                            <Switch
                              checked={!useManualDescription}
                              onChange={(e) => {
                                setUseManualDescription(!e.target.checked);
                                setSelectedSubcategoryId("");
                              }}
                              slotProps={{
                                input: {
                                  "aria-label": "Toggle description mode",
                                },
                              }}
                            />
                          </FormControl>
                        )}

                        {!useManualDescription &&
                          selectedCategoryId &&
                          subCategories.length > 0 && (
                            <FormControl>
                              <FormLabel>Subcategory</FormLabel>
                              <Select
                                placeholder="Select subcategory"
                                value={selectedSubcategoryId}
                                startDecorator={getIconForCategoryId(
                                  selectedCategoryId,
                                )}
                                onChange={(e, value) =>
                                  setSelectedSubcategoryId(value || "")
                                }
                              >
                                <Option value="">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    {getIconForCategoryName(
                                      selectedCategory?.name,
                                    )}
                                    <Typography fontFamily="monospace">
                                      None
                                    </Typography>
                                  </Stack>
                                </Option>
                                {subCategories.map((sub) => (
                                  <Option
                                    key={String(sub.id ?? "")}
                                    value={String(sub.id ?? "")}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      {/* reuse parent category icon for subcategories to keep it simple */}
                                      {getIconForCategoryName(
                                        selectedCategory?.name,
                                      )}
                                      <Typography fontFamily="monospace">
                                        {sub.name}
                                      </Typography>
                                    </Stack>
                                  </Option>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                        <Button
                          type="submit"
                          variant="outlined"
                          size={isSmall ? "md" : "lg"}
                          fullWidth
                          startDecorator={<Add sx={{color:mode==="light" ? "#454545":null}}/>}
                          sx={{
                            mt: 1,
                            color: mode === "light" ? "black" : "inherit",
                            border:
                              mode === "light"
                                ? "0.5px solid hsla(240, 7.70%, 2.50%, 0.15)"
                                : "0.5px solid rgba(229, 231, 235, 0.14)",
                            boxShadow:
                              "3.1px 6.2px 6.2px hsl(0deg 0% 0% / 0.40)",
                            "&:hover": { transform: "translateY(-2px)" },
                            transition: "all 0.3s ease",
                          }}
                        >
                          <Typography fontFamily="monospace">
                            Add Transaction
                          </Typography>
                        </Button>
                      </Stack>
                    </form>
                  </Box>
                </AccordionDetails>
              </motion.div>
            )}
          </AnimatePresence>
        </Accordion>

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: "90%", md: "55%" },
            p: { xs: 2, sm: 3 },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography level={isSmall ? "h4" : "h3"} fontFamily="monospace">
              Transactions
            </Typography>
            <Tooltip title="Search/Filter" arrow>
              <IconButton
                variant="outlined"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          </Stack>

          <AnimatePresence>
            {showFilters && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                sx={{ overflow: "hidden", mb: 3 }}
              >
                <Stack spacing={2}>
                  <Input
                    startDecorator={<Search />}
                    placeholder="Search description"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size={isSmall ? "sm" : "md"}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Select
                      placeholder="Filter category"
                      value={filterCategoryId ?? ""}
                      startDecorator={getIconForCategoryId(filterCategoryId)}
                      onChange={(e, value) => setFilterCategoryId(value || "")}
                      size={isSmall ? "sm" : "md"}
                      sx={{ flex: 1 }}
                    >
                      <Option value="">
                        <Stack direction="row" spacing={1} alignItems="center">
                          {getIconForCategoryName("None")}
                          <Typography fontFamily="monospace">
                            All Categories
                          </Typography>
                        </Stack>
                      </Option>
                      {normalizedCategories.map((c) => (
                        <Option
                          key={String(c.id ?? "")}
                          value={String(c.id ?? "")}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            {getIconForCategoryName(c.name)}
                            <Typography fontFamily="monospace">
                              {c.name}
                            </Typography>
                          </Stack>
                        </Option>
                      ))}
                    </Select>

                    <ButtonGroup
                      variant="outlined"
                      size="sm"
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Button
                        startDecorator={<Sort />}
                        variant={sortOrder === "newest" ? "solid" : "plain"}
                        onClick={() => setSortOrder("newest")}
                        sx={{ flex: { xs: 1, sm: "none" } }}
                      >
                        Newest
                      </Button>
                      <Button
                        endDecorator={
                          <Sort style={{ transform: "rotate(180deg)" }} />
                        }
                        variant={sortOrder === "oldest" ? "solid" : "plain"}
                        onClick={() => setSortOrder("oldest")}
                        sx={{ flex: { xs: 1, sm: "none" } }}
                      >
                        Oldest
                      </Button>
                    </ButtonGroup>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel sx={{ fontSize: "0.75rem" }}>From</FormLabel>
                      <Input
                        type="date"
                        value={filterFrom}
                        onChange={(e) => setFilterFrom(e.target.value)}
                        size={isSmall ? "sm" : "md"}
                      />
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel sx={{ fontSize: "0.75rem" }}>To</FormLabel>
                      <Input
                        type="date"
                        value={filterTo}
                        onChange={(e) => setFilterTo(e.target.value)}
                        size={isSmall ? "sm" : "md"}
                      />
                    </FormControl>
                  </Stack>

                  <Button
                    variant="plain"
                    size="sm"
                    onClick={clearFilters}
                    fullWidth={isSmall}
                  >
                    Clear All Filters
                  </Button>
                </Stack>
              </Box>
            )}
          </AnimatePresence>

          <Divider sx={{ my: 2 }} />

          <Typography level="body-sm" sx={{ mb: 2 }} fontFamily="monospace">
            Showing {totalItems} transaction{totalItems !== 1 ? "s" : ""}
          </Typography>

          {/* Pagination Controls (top) */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            mb={1}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  if (page > 1) {
                    setPage((p) => p - 1);
                    triggerListLoading(200);
                  }
                }}
                disabled={page <= 1}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  if (page < totalPages) {
                    setPage((p) => p + 1);
                    triggerListLoading(200);
                  }
                }}
                disabled={page >= totalPages}
              >
                Next
              </Button>
              <Typography
                level="body-xs"
                fontFamily="monospace"
                sx={{ alignSelf: "center", ml: 1 }}
              >
                Page {page} / {totalPages}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography level="body-xs" fontFamily="monospace">
                Items per page
              </Typography>
              <Select
                size="sm"
                value={String(pageSize)}
                onChange={(e, v) => {
                  const newSize = Number(v || 10);
                  setPageSize(newSize);
                  setPage(1);
                  triggerListLoading(150);
                }}
              >
                <Option value="5">5</Option>
                <Option value="10">10</Option>
                <Option value="25">25</Option>
                <Option value="50">50</Option>
              </Select>
            </Stack>
          </Stack>

          <List
            sx={{
              "--ListItem-paddingY": { xs: "12px", sm: "16px" },
              "--ListItem-paddingX": { xs: "8px", sm: "12px" },
            }}
          >
            {/* NOTE: framer-motion removed from list rendering to avoid visual glitches */}
            {isLoading ? (
              // Skeleton placeholders while loading to avoid layout jitter
              Array.from({ length: Math.min(pageSize, 8) }).map((_, i) => (
                <React.Fragment key={`skeleton-${i}`}>
                  <ListItem>
                    <ListItemContent>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <Skeleton
                          variant="rectangular"
                          sx={{
                            width: { xs: 160, sm: 240 },
                            height: 18,
                            borderRadius: 1,
                          }}
                        />
                        <Skeleton
                          variant="rectangular"
                          sx={{ width: 88, height: 18, borderRadius: 5 }}
                        />
                      </Stack>
                      <Skeleton
                        variant="rectangular"
                        sx={{ width: 120, height: 12, mt: 1, borderRadius: 1 }}
                      />
                    </ListItemContent>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexShrink: 0 }}
                    >
                      <Skeleton
                        variant="rectangular"
                        sx={{ width: 80, height: 28, borderRadius: 5 }}
                      />
                      <Skeleton
                        variant="circular"
                        sx={{ width: 32, height: 32 }}
                      />
                    </Stack>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
            ) : paginatedTransactions.length > 0 ? (
              paginatedTransactions.map((transaction, index) => (
                <React.Fragment key={txKey(transaction)}>
                  <ListItem>
                    <ListItemContent>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0.5, sm: 1 }}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Typography
                          fontFamily="monospace"
                          level={isSmall ? "body-sm" : "body-md"}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: { xs: "200px", sm: "none" },

                          }}
                        > 
                          {transaction.description || "No description"}
                        </Typography>
                        {resolveCategoryName(transaction) && (
                          <Chip
                            size="sm"
                            variant="outlined"
                            startDecorator={getIconForCategoryName(
                              resolveCategoryName(transaction),
                            )}
                            
                          >
                           <Typography fontSize={"xs"} fontFamily={"monospace"}>
                             {resolveCategoryName(transaction)}
                            </Typography>
                          </Chip>
                        )}
                      </Stack>
                      <Typography
                        level="body-xs"
                        fontFamily="monospace"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                      >
                        {formatTimestamp(
                          transaction.created_at ||
                            transaction.createdAt ||
                            transaction.dateTime ||
                            transaction.datetime ||
                            transaction._clientCreatedAt ||
                            transaction.date,
                        )}
                      </Typography>
                    </ListItemContent>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexShrink: 0 }}
                    >
                      <Chip
                        variant={mode === "light" ? "outlined" : "soft"}
                        color={transaction.amount > 0 ? "success" : "danger"}
                        size={isSmall ? "sm" : "md"}
                        startDecorator={
                          transaction.amount > 0 ? (
                            <ArrowUpward
                              sx={{ fontSize: 16, color: "green" }}
                            />
                          ) : (
                            <ArrowDownward
                              sx={{ fontSize: 16, color: "red" }}
                            />
                          )
                        }
                      >
                        <Typography
                          level={isSmall ? "body-xs" : "body-sm"}
                          sx={{
                            color: mode === "light" ? "black" : "inherit",
                          }}
                        >
                          ${Math.abs(Number(transaction.amount)).toFixed(2)}
                        </Typography>
                      </Chip>

                      <IconButton
                        variant="outlined"
                        color="danger"
                        size={isSmall ? "sm" : "md"}
                        onClick={() => handleDelete(transaction.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography
                  fontFamily="monospace"
                  color="text.secondary"
                  level="body-sm"
                >
                  No transactions found
                </Typography>
              </Box>
            )}
          </List>

          {/* Pagination Controls (bottom) */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            mt={2}
            justifyContent="space-between"
          >
            <Typography level="body-xs" fontFamily="monospace">
              Showing{" "}
              {(page - 1) * pageSize +
                Math.min(paginatedTransactions.length, pageSize)}{" "}
              of {totalItems}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  if (page > 1) {
                    setPage((p) => p - 1);
                    triggerListLoading(200);
                  }
                }}
                disabled={page <= 1}
              >
                Prev
              </Button>
              <Typography
                level="body-xs"
                fontFamily="monospace"
                sx={{ alignSelf: "center", px: 1 }}
              >
                {page} / {totalPages}
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  if (page < totalPages) {
                    setPage((p) => p + 1);
                    triggerListLoading(200);
                  }
                }}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Alerts - Bottom Left */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          zIndex: 1400,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minWidth: { xs: "calc(100% - 32px)", sm: 280 },
          maxWidth: { xs: "calc(100% - 32px)", sm: 400 },
        }}
      >
        <AnimatePresence>
          {alerts.map((a) => (
            <Alert
              component={motion.div}
              key={a.id}
              initial={{ opacity: 0, x: -100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -100, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              variant={mode === "light" ? "soft" : "solid"}
              color={a.severity === "danger" ? "danger" : a.severity}
              size="sm"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontFamily="monospace">{a.message}</Typography>
              </Box>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => removeAlert(a.id)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Alert>
          ))}
        </AnimatePresence>
      </Box>
    </CssVarsProvider>
  );
}
